import {
  Box,
  Stack,
  IconButton,
  Typography,
  Collapse,
  Divider,
  Grid,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useState } from "react";
import { ReportedDataRow } from "../../../store/slices/contentFeedback/contentFeedbackSlice";

type ReportRow = {
  heading?: any;
  row: ReportedDataRow;
  reportid: string;
  defaultOpen?: boolean;
  sx?: any;
};

function ReportedDetailsExpand({
  row,
  heading,
  reportid,
  defaultOpen,
  sx,
}: ReportRow) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  // Right side: details
  const details = [
    {
      label: "Reported By",
      value:
        `#${row.reported_by?.id} ${row.reported_by?.full_name} ${row.reported_by?.phone}` ||
        "-",
    },
    { label: "Contents", value: row.reported_contents || "-" },
    { label: "Count", value: row.total_reports },
    {
      label: "Reviewed Before",
      value: row.reviewed_before ? "Yes" : "No",
      color: row.reviewed_before ? "green" : "red",
    },
  ];

  // Left side: hierarchy
  const hierarchyDetails = [
    row.report_source && { label: "Source", value: row.report_source },
    row.goal && {
      label: "Goal",
      value: `${row.goal.name} (#${row.goal.id})`,
    },
    row.section && {
      label: "Section",
      value: `${row.section.name} (#${row.section.id})`,
    },
    row.topic && {
      label: "Topic",
      value: `${row.topic.name} (#${row.topic.id})`,
    },
    row.subtopic && {
      label: "Subtopic",
      value: `${row.subtopic.name} (#${row.subtopic.id})`,
    },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <Box
      sx={
        sx
          ? {
              ...sx,
              border: "1px solid #ccc",
              borderRadius: "8px",
              p: "0 8px",
              boxSizing:"border-box",
              color: "#4f4f4f",
            }
          : {
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxSizing:"border-box",
              p: "0 8px",
              color: "#4f4f4f",
            }
      }
    >
      {/* Row Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        minHeight={sx ? "42px" : "fit-content"}
      >
        <Box>
          <Typography
            variant="body1"
            fontWeight={"bold"}
            display="flex"
            alignItems={"center"}
          >
            {heading ?? ""} Reported Question #
            {row.question_id ?? row.question ?? ""}
          </Typography>
        </Box>
        {sx ? (
          ""
        ) : (
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            disabled={defaultOpen}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Collapsible Detailed Info */}
      <Collapse
        className={sx ? "scrollBluePrimarythin" : ""}
        sx={
          sx
            ? {
                overflowY: "auto",
                mb: open ? "8px" : "0",
                height: "calc(100% - 42px)",
                "& .MuiCollapse-wrapper": {
                  height: "100%",
                },
              }
            : { mb: open ? "8px" : "0" }
        }
        in={open}
        timeout="auto"
        unmountOnExit
      >
        <Divider sx={{ mb: "8px" }} />
        <Grid
          container
          spacing={sx?"0":2}
          sx={{ height: sx ? "calc(100% - 16px)" : "100%",mt:sx?"8px":"0" }}
        >
          {/* Left: Hierarchy */}
          <Grid item xs={12} md={defaultOpen ? 12 : 6}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Hierarchy
            </Typography>
            <Stack spacing={1}>
              {hierarchyDetails.map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1}>
                  <Typography variant="body2" fontWeight={600} minWidth={90}>
                    {item.label}:
                  </Typography>
                  <Typography variant="body2">{item.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>

          {/* Right: Other Details */}
          <Grid item xs={12} md={defaultOpen ? 12 : 6}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Report Details #{reportid}
            </Typography>
            <Stack spacing={1}>
              {details.map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1}>
                  <Typography variant="body2" fontWeight={600} minWidth={110}>
                    {item.label}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: (item as any).color || "inherit" }}
                  >
                    {item.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}

export default ReportedDetailsExpand;
